import { useState } from 'react';
import { FieldTooltip } from '../shared/FieldTooltip';

type OSType = 'windows' | 'linux' | 'mixed';

interface PreDeployInputs {
  vms: number;
  vmOS: OSType;
  appServices: number;
  storageAccounts: number;
  keyVaults: number;
  sqlDatabases: number;
  azureFirewalls: number;
  sentinelEnabled: boolean;
  planType: 'payg' | 'commitment';
}

// Daily GB/day estimates per resource (industry averages)
const VM_RATES: Record<OSType, number> = { windows: 2.0, linux: 0.5, mixed: 1.25 };
const APP_SERVICE_RATE  = 0.30;
const STORAGE_RATE      = 0.05;
const KEY_VAULT_RATE    = 0.01;
const SQL_RATE          = 0.50;
const FIREWALL_RATE     = 5.00;

// Pricing
const ANALYTICS_PAYG    = 2.1279; // £/GB
const SENTINEL_PAYG     = 3.9764; // £/GB additional
const FREE_DAILY_GB     = 5;

// Commitment tier monthly costs
const COMMITMENT_TIERS = [
  { gb: 100, monthly: 5430.60  },
  { gb: 200, monthly: 10196.10 },
  { gb: 300, monthly: 14961.90 },
  { gb: 500, monthly: 23966.40 },
];

interface ResourceRow {
  label: string;
  count: number;
  ratePerUnit: number;
  dailyGB: number;
  monthlyGB: number;
  monthlyCost: number;
}

function sliderRow(
  label: string,
  _key: keyof PreDeployInputs,
  value: number,
  min: number,
  max: number,
  onChange: (v: number) => void
) {
  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="flex justify-between items-end">
        <label className="text-[11px] font-mono text-outline uppercase tracking-wider">{label}</label>
        <span className="text-base font-bold font-mono text-primary">{value}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <div className="flex justify-between text-[10px] text-outline font-mono">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      style={checked
        ? { backgroundColor: 'var(--color-primary)', border: '1px solid var(--color-primary)' }
        : { backgroundColor: 'var(--outline-variant)', border: '1px solid var(--outline)' }}
      className="w-12 h-6 rounded-full flex items-center px-1 transition-colors"
    >
      <span
        className="w-4 h-4 rounded-full transition-transform bg-white"
        style={{ transform: checked ? 'translateX(24px)' : 'translateX(0)' }}
      />
    </button>
  );
}

export default function LogAnalyticsPreDeployCalc() {
  const [inputs, setInputs] = useState<PreDeployInputs>({
    vms: 10,
    vmOS: 'mixed',
    appServices: 5,
    storageAccounts: 5,
    keyVaults: 10,
    sqlDatabases: 5,
    azureFirewalls: 0,
    sentinelEnabled: false,
    planType: 'payg',
  });

  const set = (partial: Partial<PreDeployInputs>) =>
    setInputs((prev) => ({ ...prev, ...partial }));

  // Calculate per-resource rows
  const vmDailyGB     = inputs.vms           * VM_RATES[inputs.vmOS];
  const appDailyGB    = inputs.appServices   * APP_SERVICE_RATE;
  const storageDailyGB = inputs.storageAccounts * STORAGE_RATE;
  const kvDailyGB     = inputs.keyVaults     * KEY_VAULT_RATE;
  const sqlDailyGB    = inputs.sqlDatabases  * SQL_RATE;
  const fwDailyGB     = inputs.azureFirewalls * FIREWALL_RATE;

  const totalDailyGB  = vmDailyGB + appDailyGB + storageDailyGB + kvDailyGB + sqlDailyGB + fwDailyGB;
  const totalMonthlyGB = totalDailyGB * 30;

  // Cost calc
  const billableMonthlyGB = Math.max(0, totalMonthlyGB - FREE_DAILY_GB * 30);
  let ingestionCost = 0;
  let planLabel = '';
  let suggestedTier: typeof COMMITMENT_TIERS[0] | null = null;

  if (inputs.planType === 'payg') {
    ingestionCost = billableMonthlyGB * ANALYTICS_PAYG;
    planLabel = 'PAYG';
    // Suggest commitment tier if above break-even
    if (totalDailyGB >= 90) {
      suggestedTier = COMMITMENT_TIERS.find((t) => t.gb >= totalDailyGB) ?? COMMITMENT_TIERS[COMMITMENT_TIERS.length - 1];
    }
  } else {
    const tier = COMMITMENT_TIERS.find((t) => t.gb >= totalDailyGB) ?? COMMITMENT_TIERS[COMMITMENT_TIERS.length - 1];
    ingestionCost = tier.monthly;
    planLabel = `${tier.gb} GB/day commitment`;
  }

  const sentinelCost = inputs.sentinelEnabled ? billableMonthlyGB * SENTINEL_PAYG : 0;
  const totalCost = ingestionCost + sentinelCost;

  const paygEquivalent = billableMonthlyGB * ANALYTICS_PAYG;
  const saving = suggestedTier ? paygEquivalent - suggestedTier.monthly : 0;

  const rows: ResourceRow[] = [
    { label: `Virtual Machines (${inputs.vmOS})`, count: inputs.vms,            ratePerUnit: VM_RATES[inputs.vmOS], dailyGB: vmDailyGB,      monthlyGB: vmDailyGB * 30,      monthlyCost: (vmDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
    { label: 'App Services',                      count: inputs.appServices,   ratePerUnit: APP_SERVICE_RATE,      dailyGB: appDailyGB,     monthlyGB: appDailyGB * 30,     monthlyCost: (appDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
    { label: 'Storage Accounts',                  count: inputs.storageAccounts, ratePerUnit: STORAGE_RATE,         dailyGB: storageDailyGB, monthlyGB: storageDailyGB * 30, monthlyCost: (storageDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
    { label: 'Key Vaults',                        count: inputs.keyVaults,     ratePerUnit: KEY_VAULT_RATE,        dailyGB: kvDailyGB,      monthlyGB: kvDailyGB * 30,      monthlyCost: (kvDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
    { label: 'SQL Databases',                     count: inputs.sqlDatabases,  ratePerUnit: SQL_RATE,              dailyGB: sqlDailyGB,     monthlyGB: sqlDailyGB * 30,     monthlyCost: (sqlDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
    { label: 'Azure Firewalls',                   count: inputs.azureFirewalls, ratePerUnit: FIREWALL_RATE,        dailyGB: fwDailyGB,      monthlyGB: fwDailyGB * 30,      monthlyCost: (fwDailyGB * 30 / totalMonthlyGB || 0) * ingestionCost },
  ].filter((r) => r.count > 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full">
      {/* ── Left: Controls ── */}
      <div className="flex flex-col gap-6">
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
              rocket_launch
            </span>
            Pre-deployment Ingestion Estimator
          </h2>
          <p className="text-[12px] text-outline mb-6">
            Enter your expected resource counts to estimate Log Analytics ingestion volume before
            your workspace is live. Uses industry-average GB/day rates per resource type.
          </p>

          {/* Virtual Machines */}
          {sliderRow('Virtual Machines', 'vms', inputs.vms, 0, 500, (v) => set({ vms: v }))}
          <div className="flex gap-2 mb-6 -mt-2">
            {(['windows', 'linux', 'mixed'] as OSType[]).map((os) => (
              <button
                key={os}
                type="button"
                onClick={() => set({ vmOS: os })}
                className={`px-3 py-1.5 text-[11px] font-mono font-bold border rounded transition-all capitalize ${inputs.vmOS === os ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-container-lowest border-outline-variant text-outline hover:bg-surface-container'}`}
              >
                {os} {os === 'windows' ? '2.0 GB/d' : os === 'linux' ? '0.5 GB/d' : '1.25 GB/d'}
              </button>
            ))}
          </div>

          {sliderRow('Azure App Services', 'appServices', inputs.appServices, 0, 100, (v) => set({ appServices: v }))}
          {sliderRow('Storage Accounts', 'storageAccounts', inputs.storageAccounts, 0, 50, (v) => set({ storageAccounts: v }))}
          {sliderRow('Key Vaults', 'keyVaults', inputs.keyVaults, 0, 100, (v) => set({ keyVaults: v }))}
          {sliderRow('SQL Databases', 'sqlDatabases', inputs.sqlDatabases, 0, 50, (v) => set({ sqlDatabases: v }))}

          <div className="mb-6">
            {sliderRow('Azure Firewalls', 'azureFirewalls', inputs.azureFirewalls, 0, 20, (v) => set({ azureFirewalls: v }))}
            {inputs.azureFirewalls > 0 && (
              <p
                className="ml-1 transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Azure Firewall logs vary widely — 5 GB/day is an average; high-traffic firewalls can exceed 50 GB/day.
              </p>
            )}
          </div>

          {/* Sentinel toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-container-lowest/50 border border-outline-variant/50 rounded-sm mb-6">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-[#e8b000]" style={{ fontVariationSettings: "'FILL' 1" }}>security</span>
              <div>
                <p className="text-sm font-bold">Microsoft Sentinel</p>
                <p className="text-[10px] text-outline">+£3.98/GB on top of Log Analytics ingestion</p>
              </div>
            </div>
            <Toggle checked={inputs.sentinelEnabled} onChange={() => set({ sentinelEnabled: !inputs.sentinelEnabled })} />
          </div>

          {/* Plan type */}
          <div className="flex flex-col gap-2">
            <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Billing Plan <FieldTooltip content="Commitment Tier locks in a daily GB volume at a ~25–30% discount vs Pay-As-You-Go. Worth it above ~100 GB/day." /></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(['payg', 'commitment'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => set({ planType: p })}
                  className={`p-3 transition-all border text-sm font-bold ${inputs.planType === p ? 'bg-primary-container/10 border-primary-container text-on-surface' : 'bg-surface-container-lowest border-outline-variant text-outline hover:bg-surface-container'}`}
                >
                  {p === 'payg' ? 'Pay-As-You-Go' : 'Commitment Tier'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Accuracy warning */}
        <div className="bg-[#e8b000]/10 border border-[#e8b000]/50 rounded p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">info</span>
          <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            These estimates use industry averages and will vary significantly based on your
            diagnostic settings, log verbosity, and workload patterns. Once your workspace is
            live, use the <strong>Ingestion Cost</strong> tab with actual GB/day figures from
            the Azure portal Usage and estimated costs blade.
          </p>
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {/* Summary card */}
          <div className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-1">
                Estimated Monthly Cost (PAYG)
              </h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-bold font-headline">£{Math.round(totalCost).toLocaleString()}</span>
                <span className="text-outline text-sm">/mo</span>
              </div>
              <p className="text-[11px] text-outline">{planLabel}</p>
            </div>

            <div className="bg-surface-container-lowest/50 p-6 space-y-2">
              <div className="flex justify-between text-[11px] font-mono mb-3">
                <span className="text-outline uppercase tracking-wider">Volume</span>
                <div className="text-right">
                  <span className="font-bold text-primary">{totalDailyGB.toFixed(1)} GB/day</span>
                  <span className="text-outline ml-2">({Math.round(totalMonthlyGB).toLocaleString()} GB/mo)</span>
                </div>
              </div>
              <div className="border-t border-outline-variant/30 pt-3 space-y-2">
                <div className="flex justify-between text-xs font-mono">
                  <span className="text-outline">Log Analytics ingestion</span>
                  <span className="font-bold">£{Math.round(ingestionCost).toLocaleString()}</span>
                </div>
                {inputs.sentinelEnabled && (
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-outline">Sentinel add-on</span>
                    <span className="font-bold">£{Math.round(sentinelCost).toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-outline-variant pt-2 flex justify-between text-sm font-mono font-bold">
                  <span>Total</span>
                  <span className="text-primary">£{Math.round(totalCost).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Commitment tier recommendation */}
          {suggestedTier && inputs.planType === 'payg' && saving > 0 && (
            <div className="bg-[#0078d4]/10 border border-[#0078d4]/40 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined text-[#0078d4] text-[18px] shrink-0 mt-0.5">lightbulb</span>
                <div>
                  <p className="text-[13px] font-bold text-[#0078d4] mb-1">
                    Consider {suggestedTier.gb} GB/day commitment tier
                  </p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                    At {totalDailyGB.toFixed(0)} GB/day you are past the{' '}
                    {COMMITMENT_TIERS.find((t) => t.gb === suggestedTier!.gb) ? suggestedTier.gb : ''}
                    {' '}GB break-even point.
                  </p>
                  <p className="text-[13px] font-bold text-[#107c10] mt-1">
                    Estimated saving: ~£{Math.round(saving).toLocaleString()}/month
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Per-resource breakdown */}
          {rows.length > 0 && (
            <div className="bg-surface-container-high ghost-border rounded-lg p-5">
              <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-4">
                Ingestion by Resource Type
              </h4>
              <div className="flex flex-col gap-2">
                {rows.map((row) => (
                  <div key={row.label} className="flex flex-col gap-0.5">
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-outline">{row.label} ×{row.count}</span>
                      <span className="font-mono font-bold text-on-surface">
                        {row.dailyGB.toFixed(1)} GB/day
                      </span>
                    </div>
                    <div className="flex justify-between text-[10px] text-outline/60 font-mono">
                      <span>{row.ratePerUnit} GB/unit/day</span>
                      <span>~{Math.round(row.monthlyGB).toLocaleString()} GB/mo</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-surface-container-lowest/50 border border-outline-variant rounded p-4">
            <p className="text-[12px] text-outline leading-relaxed">
              <span className="text-primary font-semibold">Once your workspace is live,</span>{' '}
              use the <strong className="text-on-surface">Ingestion Cost</strong> tab for precise
              calculations based on your actual GB/day figures from the Azure portal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
